import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter
} from '@/components/ui/card';
import { 
  Form, 
  FormControl, 
  FormDescription, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ArrowLeft, Save, Code } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';

// Interfaces
interface EmailTemplate {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TemplateVersion {
  id: string;
  email_template_id: string;
  version_number: number;
  subject: string;
  html_content: string;
  plain_text_content: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
}

interface PreviewData {
  name: string;
  email: string;
  company: string;
  date: string;
  link: string;
  code: string;
  product: string;
  amount: string;
  invoice_number: string;
  [key: string]: string;
}

// Validation schema
const versionSchema = z.object({
  subject: z.string().min(3, { message: 'O assunto deve ter pelo menos 3 caracteres' }),
  html_content: z.string().min(10, { message: 'O corpo do email deve ter pelo menos 10 caracteres' }),
  active: z.boolean().default(true)
});

type VersionFormValues = z.infer<typeof versionSchema>;

const AddTemplateVersion = () => {
  const { templateId, versionId } = useParams<{ templateId: string; versionId?: string }>();
  const navigate = useNavigate();
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [existingVersions, setExistingVersions] = useState<TemplateVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [isExampleDialogOpen, setIsExampleDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<TemplateVersion | null>(null);

  // Form for template version
  const form = useForm<VersionFormValues>({
    resolver: zodResolver(versionSchema),
    defaultValues: {
      subject: '',
      html_content: '',
      active: true
    }
  });

  // Watch html_content field for live preview
  const htmlContent = form.watch('html_content');
  const subject = form.watch('subject');

  useEffect(() => {
    // Clear form on initial load
    form.reset({
      subject: '',
      html_content: '',
      active: true
    });

    if (templateId) {
      fetchTemplate();
      fetchExistingVersions();
      
      // Check if we're in edit mode
      if (versionId) {
        console.log('Edit mode detected, version ID:', versionId);
        setIsEditMode(true);
        fetchVersionDetails(versionId);
      } else {
        setIsEditMode(false);
        setCurrentVersion(null);
        setLoading(false);
      }
    }
  }, [templateId, versionId]);

  // Update preview whenever html_content changes
  useEffect(() => {
    generatePreview(htmlContent);
  }, [htmlContent]);

  const fetchTemplate = async () => {
    if (!templateId) return;
    
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .eq('id', templateId)
        .single();
        
      if (error) throw error;
      console.log('Template fetched:', data);
      setTemplate(data);
    } catch (error: any) {
      console.error('Erro ao buscar template:', error);
      toast.error('Erro ao carregar informações do template', {
        description: error.message
      });
    }
  };

  const fetchExistingVersions = async () => {
    if (!templateId) return;
    
    try {
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('email_template_id', templateId)
        .order('version_number', { ascending: false });
        
      if (error) throw error;
      console.log('Existing versions:', data);
      setExistingVersions(data || []);
    } catch (error: any) {
      console.error('Erro ao buscar versões existentes:', error);
    }
  };

  const fetchVersionDetails = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('email_template_versions')
        .select('*')
        .eq('id', id)
        .single();
        
      if (error) throw error;
      
      console.log('Version details fetched:', data);
      if (data) {
        setCurrentVersion(data);
        // Populate form with existing data
        form.reset({
          subject: data.subject,
          html_content: data.html_content,
          active: data.active
        });
      }
    } catch (error: any) {
      console.error('Erro ao buscar detalhes da versão:', error);
      toast.error('Erro ao carregar detalhes da versão do template', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const generatePreview = (html: string) => {
    try {
      // Sample data for preview
      const previewData: PreviewData = {
        name: 'João Silva',
        email: 'joao.silva@exemplo.com',
        company: 'Empresa ABC Ltda',
        date: new Date().toLocaleDateString('pt-BR'),
        link: 'https://exemplo.com/confirmar',
        code: '123456',
        product: 'Plano Premium',
        amount: 'R$ 99,90',
        invoice_number: 'INV-2023-001'
      };
      
      // Replace variables in template
      let previewContent = html;
      
      // Replace variables in format {{variable}}
      Object.keys(previewData).forEach(key => {
        const regex = new RegExp(`{{${key}}}`, 'g');
        previewContent = previewContent.replace(regex, previewData[key]);
      });
      
      // Add basic styles for preview with improved styling for email rendering
      const styledHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              line-height: 1.6; 
              color: #333;
              margin: 0;
              padding: 0;
            }
            .email-container {
              max-width: 600px;
              margin: 0 auto;
              padding: 20px;
              border: 1px solid #e2e8f0;
              border-radius: 5px;
              background-color: #fff;
            }
            a { color: #3182ce; text-decoration: underline; }
            .btn {
              display: inline-block;
              padding: 10px 20px;
              background-color: #3182ce;
              color: white !important;
              text-decoration: none;
              border-radius: 5px;
              font-weight: bold;
            }
            h1, h2, h3, h4, h5, h6 {
              margin-top: 0;
              margin-bottom: 0.5em;
              line-height: 1.2;
              color: #1a202c;
            }
            p {
              margin-top: 0;
              margin-bottom: 1em;
            }
            img {
              max-width: 100%;
              height: auto;
            }
            table {
              border-collapse: collapse;
              width: 100%;
            }
            td {
              vertical-align: top;
              padding: 0;
            }
          </style>
        </head>
        <body>
          <div class="email-container">
            ${previewContent || '<p>Sem conteúdo para preview</p>'}
          </div>
        </body>
        </html>
      `;
      
      setPreviewHtml(styledHtml);
    } catch (error) {
      console.error('Erro ao gerar preview:', error);
      toast.error('Erro ao gerar preview do template');
    }
  };

  const onSubmit = async (values: VersionFormValues) => {
    if (!templateId) return;
    
    setSubmitting(true);
    try {
      if (isEditMode && currentVersion) {
        console.log('Updating existing version:', currentVersion.id);
        // Update existing version
        if (values.active) {
          // If setting this version to active, deactivate others first
          const { error: deactivateError } = await supabase
            .from('email_template_versions')
            .update({ active: false })
            .eq('email_template_id', templateId)
            .neq('id', currentVersion.id);
            
          if (deactivateError) throw deactivateError;
        }
        
        // Update the current version
        const { error: updateError } = await supabase
          .from('email_template_versions')
          .update({
            subject: values.subject,
            html_content: values.html_content,
            plain_text_content: null,
            active: values.active,
            updated_at: new Date().toISOString()
          })
          .eq('id', currentVersion.id);
          
        if (updateError) throw updateError;
        
        toast.success('Versão do template atualizada com sucesso');
      } else {
        // Create new version
        console.log('Creating new version for template:', templateId);
        // Determine next version number
        let nextVersionNumber = 1;
        
        if (existingVersions.length > 0) {
          // If there are previous versions, find the highest version number and increment
          nextVersionNumber = Math.max(...existingVersions.map(v => v.version_number)) + 1;
          
          // If new version should be active, deactivate previous ones
          if (values.active) {
            const { error: deactivateError } = await supabase
              .from('email_template_versions')
              .update({ active: false })
              .eq('email_template_id', templateId);
              
            if (deactivateError) throw deactivateError;
          }
        }
        
        // Create new template version
        const versionData = {
          email_template_id: templateId,
          version_number: nextVersionNumber,
          subject: values.subject,
          html_content: values.html_content,
          plain_text_content: null, // Optional
          active: values.active,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { error: insertError } = await supabase
          .from('email_template_versions')
          .insert([versionData]);
          
        if (insertError) throw insertError;
        
        toast.success('Versão do template criada com sucesso');
      }
      
      navigate('/email-templates');
    } catch (error: any) {
      console.error(`Erro ao ${isEditMode ? 'atualizar' : 'criar'} versão do template:`, error);
      toast.error(`Erro ao ${isEditMode ? 'atualizar' : 'criar'} versão do template`, {
        description: error.message
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full px-6 py-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex justify-center">
              <p>Carregando...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="w-full px-6 py-6">
        <Card>
          <CardContent className="pt-6">
            <Alert variant="destructive">
              <AlertDescription>
                Template não encontrado ou você não tem permissão para acessá-lo.
              </AlertDescription>
            </Alert>
            <div className="mt-4">
              <Button variant="outline" onClick={() => navigate('/email-templates')}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Voltar para Templates
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/email-templates')}
              className="mb-2"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
          </div>
          <h1 className="text-3xl font-extrabold">
            {isEditMode ? 'Editar' : 'Adicionar'} Versão do Template
          </h1>
          <p className="text-muted-foreground">
            {template.name}
            {isEditMode && currentVersion && ` - Versão ${currentVersion.version_number}`}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assunto do Email</FormLabel>
                  <FormControl>
                    <Input placeholder="Ex: Bem-vindo ao Sistema" {...field} />
                  </FormControl>
                  <FormDescription>
                    O assunto que será exibido na linha de assunto do email
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="active"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Switch 
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="cursor-pointer">Versão Ativa</FormLabel>
                  <p className="text-sm text-muted-foreground ml-auto">
                    Apenas uma versão pode estar ativa por vez
                  </p>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* HTML EDITOR - LEFT SIDE */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle>Editor HTML</CardTitle>
                <CardDescription className="flex justify-between items-center">
                  <span>Crie o conteúdo do seu template usando HTML</span>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsExampleDialogOpen(true)}
                    className="ml-2 gap-1"
                  >
                    <Code className="h-3.5 w-3.5" /> Ver Exemplo
                  </Button>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  control={form.control}
                  name="html_content"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Textarea 
                          placeholder="<!DOCTYPE html>..."
                          className="h-[600px] font-mono resize-none"
                          {...field} 
                        />
                      </FormControl>
                      <FormDescription>
                        Você pode usar HTML e variáveis como {'{{name}}'}, {'{{email}}'}, etc.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-6">
                  <h3 className="text-sm font-extrabold mb-1">Variáveis disponíveis:</h3>
                  <div className="bg-muted p-3 rounded text-sm">
                    <ul className="space-y-1">
                      <li><code>{'{{name}}'}</code> - Nome do destinatário</li>
                      <li><code>{'{{email}}'}</code> - Email do destinatário</li>
                      <li><code>{'{{company}}'}</code> - Empresa do destinatário</li>
                      <li><code>{'{{date}}'}</code> - Data atual formatada</li>
                      <li><code>{'{{link}}'}</code> - Link personalizado</li>
                      <li><code>{'{{code}}'}</code> - Código de confirmação</li>
                      <li><code>{'{{product}}'}</code> - Nome do produto</li>
                      <li><code>{'{{amount}}'}</code> - Valor monetário</li>
                      <li><code>{'{{invoice_number}}'}</code> - Número da fatura</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* PREVIEW - RIGHT SIDE */}
            <Card className="lg:col-span-1">
              <CardHeader className="pb-3">
                <CardTitle>Preview</CardTitle>
                <CardDescription>
                  Visualização em tempo real do seu template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md h-[600px] overflow-hidden">
                  <div className="border-b p-3 bg-muted/30">
                    <div className="text-sm">
                      <span className="font-extrabold">De:</span> Sistema &lt;sistema@exemplo.com&gt;
                    </div>
                    <div className="text-sm">
                      <span className="font-extrabold">Para:</span> João Silva &lt;joao.silva@exemplo.com&gt;
                    </div>
                    <div className="text-sm">
                      <span className="font-extrabold">Assunto:</span> {subject || '(Sem assunto)'}
                    </div>
                  </div>
                  <div className="email-preview overflow-auto" style={{ height: 'calc(600px - 84px)' }}>
                    {previewHtml ? (
                      <iframe 
                        srcDoc={previewHtml}
                        title="Email Preview"
                        className="w-full h-full border-0"
                        sandbox="allow-same-origin"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        Adicione conteúdo HTML para visualizar o preview
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-6">
                  <h3 className="text-sm font-extrabold mb-1">Dicas para templates:</h3>
                  <ul className="text-sm space-y-2 pl-5 list-disc">
                    <li>Mantenha o design responsivo para visualização em dispositivos móveis</li>
                    <li>Use tabelas para garantir compatibilidade com clientes de email mais antigos</li>
                    <li>Evite imagens de fundo e CSS complexo que podem não funcionar em todos os clientes</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="pt-4 flex justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/email-templates')}
              className="mr-2"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting} className="gap-1">
              <Save className="h-4 w-4" />
              {submitting ? (isEditMode ? 'Salvando...' : 'Criando...') : (isEditMode ? 'Salvar Alterações' : 'Criar Versão')}
            </Button>
          </div>
        </form>
      </Form>

      {/* Exemplo de estrutura básica como modal */}
      <Dialog open={isExampleDialogOpen} onOpenChange={setIsExampleDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Exemplo de estrutura básica</DialogTitle>
            <DialogDescription>
              Modelo básico de template HTML para emails
            </DialogDescription>
          </DialogHeader>
          
          <div className="my-4">
            <pre className="bg-muted p-4 rounded text-xs overflow-auto max-h-96">
{`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>{{subject}}</title>
</head>
<body>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td style="padding: 20px; text-align: center;">
              <h1>Olá {{name}},</h1>
              <p>Seu conteúdo aqui...</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`}
            </pre>
          </div>
          
          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                const exampleTemplate = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>{{subject}}</title>
</head>
<body>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td style="padding: 20px; text-align: center;">
              <h1>Olá {{name}},</h1>
              <p>Seu conteúdo aqui...</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
                form.setValue('html_content', exampleTemplate);
                setIsExampleDialogOpen(false);
              }}
            >
              Usar este exemplo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AddTemplateVersion; 